import React, { forwardRef } from "react";
import { RANKS } from "../lib/api";
import { SKIN_STYLES } from "../lib/skins";
import { useLang } from "../i18n/LanguageContext";

/* Carte de carrière partageable — rendu natif 1080x1920 (format story). */
export const CareerCard = forwardRef(({ result, domainLabel, url, skin }, ref) => {
  const { dict } = useLang();
  const rankKey = result.rank || "anonyme";
  const rc = RANKS[rankKey];
  const theme = skin && SKIN_STYLES[skin] ? SKIN_STYLES[skin] : rc;
  const c1 = theme.c1;
  const c2 = theme.c2;
  const glow = theme.glow;
  const cardBg =
    skin && SKIN_STYLES[skin]
      ? SKIN_STYLES[skin].bg
      : "radial-gradient(900px 500px at 50% 0%, rgba(240,147,251,0.15), transparent 55%), linear-gradient(160deg, #1a1a2e 0%, #16213e 100%)";
  const rankLabel = dict.ranks[rankKey];
  const rankTitle = dict.rankTitles[rankKey];
  const punch = dict.punchlines[rankKey];
  const host = (url || "").replace(/^https?:\/\//, "").replace(/\/$/, "");

  const stats = [
    { label: dict.argent, value: `${(result.argent || 0).toLocaleString()}€` },
    { label: dict.gloire, value: result.gloire || 0 },
    { label: dict.energie, value: result.energie || 0 },
    { label: dict.chance, value: result.chance || 0 },
  ];

  return (
    <div
      ref={ref}
      style={{
        width: 1080,
        height: 1920,
        background: cardBg,
        fontFamily: "'Outfit', sans-serif",
        position: "relative",
        overflow: "hidden",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "90px 70px",
      }}
    >
      {/* Bordure néon */}
      <div
        style={{
          position: "absolute",
          inset: 40,
          border: `4px solid ${c1}`,
          borderRadius: 48,
          boxShadow: `0 0 60px ${glow}, inset 0 0 60px ${glow}`,
        }}
      />
      {/* Particules */}
      {Array.from({ length: 40 }).map((_, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${(i * 47) % 100}%`,
            top: `${(i * 83) % 100}%`,
            width: ((i % 3) + 1) * 3,
            height: ((i % 3) + 1) * 3,
            borderRadius: "50%",
            background: c1,
            opacity: 0.25,
          }}
        />
      ))}

      <div style={{ position: "relative", zIndex: 2, width: "100%", textAlign: "center" }}>
        <p style={{ fontFamily: "'Unbounded', sans-serif", fontSize: 46, fontWeight: 800, letterSpacing: 2 }}>
          DESTIN EXPRESS
        </p>
        <p style={{ fontSize: 30, color: "rgba(255,255,255,0.55)", marginTop: 8 }}>{domainLabel}</p>

        <div style={{ marginTop: 70, fontSize: 34, color: "rgba(255,255,255,0.7)", letterSpacing: 4 }}>
          🏆 {dict.yourDestiny.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "'Unbounded', sans-serif",
            fontSize: 150,
            fontWeight: 900,
            lineHeight: 1.05,
            marginTop: 10,
            background: `linear-gradient(90deg, ${c1}, ${c2})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {rankLabel}
        </div>
        <p style={{ fontSize: 40, fontStyle: "italic", color: "#fff", marginTop: 6 }}>{rankTitle}</p>

        <div style={{ margin: "60px auto 0", height: 2, width: 220, background: "rgba(255,255,255,0.15)" }} />

        <div
          style={{
            fontFamily: "'Unbounded', sans-serif",
            fontSize: 200,
            fontWeight: 900,
            marginTop: 40,
            background: `linear-gradient(90deg, ${c1}, ${c2})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {(result.score || 0).toLocaleString()}
        </div>
        <p style={{ fontSize: 36, letterSpacing: 8, color: "rgba(255,255,255,0.45)" }}>{dict.points}</p>

        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 24, marginTop: 60 }}>
          {stats.map((s) => (
            <div
              key={s.label}
              style={{
                width: 420,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 24,
                padding: "24px 30px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 36, color: "rgba(255,255,255,0.55)" }}>{s.label}</span>
              <span style={{ fontSize: 40, fontWeight: 700 }}>{s.value}</span>
            </div>
          ))}
        </div>

        <p
          style={{
            fontSize: 40,
            fontStyle: "italic",
            color: c1,
            marginTop: 70,
            lineHeight: 1.4,
            padding: "0 20px",
          }}
        >
          « {punch} »
        </p>
      </div>

      <div style={{ position: "absolute", bottom: 90, left: 0, right: 0, textAlign: "center", zIndex: 2 }}>
        <p style={{ fontSize: 34, color: "rgba(255,255,255,0.5)" }}>{result.pseudo || "Anonyme"}</p>
        <p style={{ fontFamily: "'Unbounded', sans-serif", fontSize: 34, fontWeight: 700, color: c1, marginTop: 10 }}>
          #DestinExpress
        </p>
        {host && (
          <p style={{ fontSize: 28, color: "rgba(255,255,255,0.4)", marginTop: 14 }}>{host}</p>
        )}
      </div>
    </div>
  );
});

CareerCard.displayName = "CareerCard";
